/* eslint-disable jsx-a11y/anchor-is-valid */
import { useMemo, type FC, type PropsWithRef } from "react";
import {
  DarkThemeToggle,
  Navbar,
  Dropdown,
  Spinner,
  Tooltip,
} from "flowbite-react";
import { HiMenuAlt1, HiX, HiChevronDown } from "react-icons/hi";
import { useSidebarContext } from "../../context/SidebarContext";
import { useDarkMode } from "../../context/DarkModeContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import isSmallScreen from "../../helpers/is-small-screen";
import { UserDropdown, AppDrawerDropdown, Button } from "@components";
import { IUser } from "../../types";
import primaryLogo from "../../assets/logo/logibud_primary_darkblue.svg";
import secondaryLogo from "../../assets/logo/logibud_secondary_white.svg";
import { useUserWorkspaces, useOnboardingStatus } from "../../hooks";
import { useTranslation } from "react-i18next";
import { useUserSession } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import PlusIcon from "../../assets/icons/plus_icon.svg";

export const NavigationBar: FC<PropsWithRef<IUser>> = function (user) {
  const { t } = useTranslation(["workspace", "orders", "onboarding"]);
  const { canCreateOrder, isResolving } = useOnboardingStatus();
  // Only disable once data has resolved, so we never wrongly block a workspace
  // whose customers/drivers simply have not loaded yet.
  const isNewOrderDisabled = !isResolving && !canCreateOrder;
  const { isDarkMode, setDarkMode } = useDarkMode();
  const { isOpenOnSmallScreens, isPageWithSidebar, setOpenOnSmallScreens } =
    useSidebarContext();
  const { activeWorkspace, setActiveWorkspace } = useWorkspace();

  const { userSession } = useUserSession();
  const query = useUserWorkspaces(userSession?.user.email ?? "");

  const isQueryLoading = useMemo(() => query.isLoading, [query.isLoading]);

  const queryData = useMemo(() => query.data ?? [], [query]);

  // Handle workspace selection
  const handleWorkspaceChange = (workspaceId: string) => {
    const selectedWorkspace = queryData?.find(
      (w) => w.workspaceId === workspaceId,
    );
    if (selectedWorkspace) {
      setActiveWorkspace(selectedWorkspace);
    }
  };

  const navigate = useNavigate();
  const handleNewOrderClick = () => {
    navigate("/orders/new");
  };
  return (
    <Navbar
      fluid
      className="fixed z-30 w-full border-b border-gray-200 dark:bg-layout dark:border-gray-700"
    >
      <div className="w-full p-3 lg:px-5 lg:pl-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {isPageWithSidebar && (
              <button
                onClick={() => setOpenOnSmallScreens(!isOpenOnSmallScreens)}
                className="mr-3 cursor-pointer rounded p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-muted dark:hover:bg-gray-700 dark:hover:text-white lg:inline"
              >
                <span className="sr-only">Toggle sidebar</span>
                {isOpenOnSmallScreens && isSmallScreen() ? (
                  <HiX className="h-6 w-6" />
                ) : (
                  <HiMenuAlt1 className="h-6 w-6" />
                )}
              </button>
            )}
            <Navbar.Brand href="/">
              <img
                alt="Logibud"
                src={isDarkMode ? secondaryLogo : primaryLogo}
                className="mr-3 h-6 sm:h-8"
              />
            </Navbar.Brand>
          </div>
          <div className="flex items-center lg:gap-3">
            <div className="flex items-center">
              {!isSmallScreen() ? (
                isQueryLoading ? (
                  <Spinner />
                ) : query.isError ? (
                  <div className="text-red-500">
                    {t("error_loading_workspace")}
                  </div>
                ) : (
                  <Dropdown
                    inline
                    arrowIcon={false}
                    label={
                      <div className="active:scale-95 hidden md:flex mr-4  items-center gap-4 bg-white shadow-sm rounded-lg pl-4 p-3 dark:bg-surface h-full hover:border-gray-200 dark:hover:bg-layout cursor-pointer border-gray-100 border-solid border-2 dark:border-border-subtle dark:border-solid dark:border-2">
                        <span className="text-muted flex-1">
                          {activeWorkspace?.workspaceName ||
                            t("select_workspace")}
                        </span>
                        <HiChevronDown className="text-muted" />
                      </div>
                    }
                  >
                    {queryData?.map((workspace) => (
                      <Dropdown.Item
                        key={workspace.workspaceId}
                        onClick={() =>
                          handleWorkspaceChange(workspace.workspaceId)
                        }
                      >
                        {workspace.workspaceName}
                      </Dropdown.Item>
                    )) || (
                      <Dropdown.Item disabled>
                        {t("no_workspace_available")}
                      </Dropdown.Item>
                    )}
                  </Dropdown>
                )
              ) : null}
              {isNewOrderDisabled ? (
                <Tooltip content={t("onboarding:order_locked_tooltip")}>
                  <span className="hidden-on-small me-4 inline-flex cursor-not-allowed">
                    <Button
                      disabled
                      className="active:scale-95 bg-brand font-medium rounded-lg text-sm px-1 py-1 text-center inline-flex items-center opacity-60"
                    >
                      <img
                        src={PlusIcon}
                        className="w-3.5 h-3.5 me-2"
                        alt="New Order Icon"
                      />
                      {t("orders:create.new")}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  onClick={handleNewOrderClick}
                  className="hidden-on-small active:scale-95 bg-brand hover:bg-lgb-blue-350 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-1 py-1 text-center inline-flex items-center me-4 dark:focus:ring-blue-800"
                >
                  <img
                    src={PlusIcon}
                    className="w-3.5 h-3.5 me-2"
                    alt="New Order Icon"
                  />
                  {t("orders:create.new")}
                </Button>
              )}

              <div className="hidden">
                <AppDrawerDropdown />
              </div>
              <DarkThemeToggle onClick={() => setDarkMode(!isDarkMode)} />

              <div className="ml-2">
                <UserDropdown {...user} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Navbar>
  );
};
